import React, { useEffect, useState } from 'react'
import {
  Modal,
  Search,
  InputWithIcon,
  CheckBox,
  Input,
  ButtonWithIcon,
  ModalControlsStyled,
  Button,
  ComponentLoader,
} from '@clickcease/cc-client-components-lib'

import { useTranslation } from 'react-i18next'
import {
  DomainReactivationError,
  DomainReactivationList,
  DomainReactivationListHeader,
  DomainReactivationListHeaderTitle,
  DomainReactivationListHeaderTitleWrapper,
  DomainReactivationModalInner,
  DomainReactivationModalSearch,
  DomainReactivationNewDomainWrapper,
} from 'Features/ReactivationPage/DomainReactivationModal/DomainReactivationModal.styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCirclePlus } from '@fortawesome/pro-thin-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useGetInactiveDomains } from 'Features/domainsManagement/api/getInactiveDomains'
import { Domain, ReactivateDomains, ReactivateDomainsToSetup } from 'Types/Domain'
import { DomainReactivationListRows } from 'Features/ReactivationPage/DomainReactivationModal/components/DomainReactivationList'
import { validateDomain } from 'Utils/validation'
import { useReactivationDomain } from 'Features/ReactivationPage/api/reactivateDomain'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { useGetErrorText } from 'Utils/errorText'
import { SetupDomains } from 'Features/MainWrapper/CrossSaleModal/steps/SetupDomains'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { trimPrototcolAndWww } from 'Utils/routes'
export const DomainReactivationModal = () => {
  const { t } = useTranslation('accountSettings')
  const inactiveDomainsData = useGetInactiveDomains()
  const [searchText, setSearchText] = useState<string>('')
  const { data: activatedDomains } = useGetDomains()
  const [localDomains, setLocalDomains] = useState<ReactivateDomains[]>([])
  const [domainsToSetup, setDomainsToSetup] = useState<ReactivateDomainsToSetup[]>([])
  const [allDomainsSelected, setAllDomainsSelected] = useState<boolean>(false)
  const [newDomain, setNewDomain] = useState<string>('')
  const [finishedReactivation, setFinishedReactivation] = useState<boolean>(false)
  const [serverError, setServerError] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const { mutate: reactivateDomains, isLoading } = useReactivationDomain()
  const { showToaster, setModalToShow, setDisablePlatformConnectionModal } = useAppStore()
  const { data: accountLimits } = useGetPackagingLimits()
  const { getErrorText } = useGetErrorText()
  const domainsToActivate: Array<ReactivateDomains> = []

  //domains to show in the list
  useEffect(() => {
    const domains = inactiveDomainsData?.data?.data?.domains
    domains?.forEach((domain) => {
      domain.selected = false
      domain.showInSearch = true
    })
    setLocalDomains(domains || [])
  }, [inactiveDomainsData?.data?.data?.domains])

  //domains that where activated and need to be setup
  useEffect(() => {
    const domains: Array<ReactivateDomainsToSetup> = []
    activatedDomains?.data?.domains.forEach((domain: Domain) => {
      domains.push({ ...domain, isSelected: false, wordpress: false })
    })
    setDomainsToSetup(domains || [])
  }, [activatedDomains?.data?.domains])

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
    if (e.target.value.length > 0) {
      setLocalDomains((prev) => {
        return prev.map((domain) =>
          domain.address.includes(e.target.value)
            ? { ...domain, showInSearch: true }
            : { ...domain, showInSearch: false }
        )
      })
    } else {
      setLocalDomains((prev) => {
        return prev.map((domain) => {
          return { ...domain, showInSearch: true }
        })
      })
    }
  }

  const selectDomain = (domainId: number) => {
    setLocalDomains((prev) => {
      return prev.map((domain) =>
        domain.id === domainId ? { ...domain, selected: !domain.selected } : domain
      )
    })
  }

  const selectDomainProducts = (domainId: number, paidMarketing: boolean, botZapping: boolean) => {
    setLocalDomains((prev) => {
      return prev.map((domain) =>
        domain.id === domainId ? { ...domain, paidMarketing, botZapping } : domain
      )
    })
  }

  const selectAllDomains = () => {
    setLocalDomains((prev) => {
      return prev.map((domain) => ({ ...domain, selected: !allDomainsSelected }))
    })
    setAllDomainsSelected(!allDomainsSelected)
  }

  const handleAddDomainInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewDomain(e.target.value)
  }

  //add new domain to the list of domains to activate
  const addNewDomain = () => {
    if (validateDomain(newDomain)) {
      setError('')
      setLocalDomains((prev) => {
        return [
          ...prev,
          {
            id: Math.random(),
            address: trimPrototcolAndWww(newDomain),
            selected: true,
            paidMarketing: accountLimits?.paidMarketingAllowed || false,
            botZapping: accountLimits?.botZappingAllowed || false,
            showInSearch: true,
          },
        ]
      })
      setDisablePlatformConnectionModal(false)
      setNewDomain('')
    } else {
      setServerError(false)
      setError(t('domain_reactivation_modal.domain_not_valid'))
    }
  }

  //activate selected domain and show setup modal
  const activateClicked = () => {
    setError('')
    setServerError(false)

    //get domains to activate
    localDomains.forEach((domain) => {
      if (domain.selected) domainsToActivate.push(domain)
    })

    if (domainsToActivate.length > 0) {
      reactivateDomains(
        { domains: localDomains },
        {
          onSuccess: () => {
            setFinishedReactivation(true)
            showToaster(t('domain_reactivation_modal.domain_reactivated_successfully'), 'success')
          },
          onError: (res) => {
            setError(getErrorText(res) || '')
            setServerError(true)
          },
        }
      )
    } else {
      setServerError(false)
      setError(t('domain_reactivation_modal.at_least_one_domain'))
    }
  }

  return (
    <Modal
      title={t('domain_reactivation_modal.title')}
      isOpen={true}
      onOpenChange={() => {}}
      onCloseClick={() => {
        setModalToShow('')
      }}
    >
      {finishedReactivation ? (
        <SetupDomains
          isBotMitigation={false}
          domains={domainsToSetup}
          onDone={() => {
            setModalToShow('')
          }}
        />
      ) : (
        <DomainReactivationModalInner>
          {inactiveDomainsData.isLoading ? (
            <ComponentLoader css={{ position: 'absolute' }} />
          ) : (
            <>
              <DomainReactivationModalSearch>
                <InputWithIcon
                  css={{ width: '250px', height: '32px' }}
                  value={searchText}
                  placeholder={t('domain_reactivation_modal.search')}
                  onChange={onSearchInputChange}
                  iconLeft={<Search />}
                />
              </DomainReactivationModalSearch>
              <DomainReactivationList>
                <DomainReactivationListHeader>
                  {localDomains.length > 0 && (
                    <CheckBox
                      containerCSS={{ marginTop: '21px' }}
                      isChecked={allDomainsSelected}
                      onClick={selectAllDomains}
                      disabled={false}
                    />
                  )}
                  <DomainReactivationListHeaderTitleWrapper>
                    <DomainReactivationListHeaderTitle>
                      {t('domain_reactivation_modal.domains')}
                    </DomainReactivationListHeaderTitle>
                    <DomainReactivationListHeaderTitle>
                      {t('domain_reactivation_modal.products')}
                    </DomainReactivationListHeaderTitle>
                  </DomainReactivationListHeaderTitleWrapper>
                </DomainReactivationListHeader>
                <DomainReactivationListRows
                  selectDomainProduct={selectDomainProducts}
                  domains={localDomains}
                  selectDomain={selectDomain}
                />
                <DomainReactivationNewDomainWrapper>
                  <Input
                    placeholder={t('domain_reactivation_modal.your_new_domain')}
                    value={newDomain}
                    onChange={handleAddDomainInput}
                    style={{ width: '200px', marginRight: '6px' }}
                  />
                  <ButtonWithIcon
                    iconLeft={<FontAwesomeIcon icon={faCirclePlus as IconProp} />}
                    text={t('domain_reactivation_modal.add')}
                    variant={'outline'}
                    onClick={addNewDomain}
                  />
                  <DomainReactivationError serverError={serverError}>
                    {error}
                  </DomainReactivationError>
                </DomainReactivationNewDomainWrapper>
              </DomainReactivationList>
              <ModalControlsStyled>
                <Button
                  disabled={isLoading}
                  loading={isLoading}
                  text={t('domain_reactivation_modal.activate')}
                  onClick={activateClicked}
                />
              </ModalControlsStyled>
            </>
          )}
        </DomainReactivationModalInner>
      )}
    </Modal>
  )
}
